// ============================================================
// Wegweisung und Gerüst (SPEC §13.5). Muster: lilasprojekte App.tsx.
//
// Vier Ebenen, jede mit genau einer Aufgabe:
//   Wurzel       Meldungen und der EINE Aufruf von GET /me
//   Geschuetzt   alles außer /anmelden: Sitzung nötig, sonst zur Anmeldung
//   Shell        Kopfleiste + Reiterleiste um jeden Bildschirm
//   Bildschirm   Auffangkorb + Nachladeanzeige je Weg
//
// Jeder Bildschirm wird TRÄGE über seinen Pfad geladen (§13.5: Pfade und
// Standardexporte sind Vertrag). Die Anmeldung bleibt eifrig geladen — sie ist für
// jeden Abgemeldeten der Weg hinein, und ein Nachladeschritt davor wäre ein leerer
// Bildschirm ohne Grund.
//
// Der Auffangkorb liegt ÜBER dem Outlet, aber UNTER der Kopfleiste: stürzt ein
// Bildschirm ab, soll man wegnavigieren können. Sein `key` ist der Pfad — ohne ihn
// bliebe ein abgestürzter Bildschirm auch nach dem Wegklicken als Fehlerseite stehen.
// ============================================================
import { Suspense, lazy, useEffect } from "react";
import { createBrowserRouter, Navigate, Outlet, RouterProvider, useLocation, useNavigate } from "react-router-dom";
import { Button, Spinner, Toaster } from "./components/ui";
import { ScreenBoundary } from "./components/app/ScreenBoundary";
import { Shell } from "./components/app/Shell";
import { Anmelden } from "./screens/Anmelden";
import { useQueryClient } from "@tanstack/react-query";
import { istApiFehler } from "./api/client";
import { useIch } from "./api/hooks/basis";
import { schluessel } from "./api/schluessel";
import { useTitel } from "./hooks/useTitel";
import { useT } from "./lib/texte";
import { istAdmin } from "./lib/rechte";
import { useAuthStore } from "./store/useAuthStore";

const Kataloge = lazy(() => import("./screens/kataloge/Kataloge"));
const NeuerKatalog = lazy(() => import("./screens/kataloge/NeuerKatalog"));
const Geloescht = lazy(() => import("./screens/kataloge/Geloescht"));
const Editor = lazy(() => import("./screens/editor/Editor"));
const Staende = lazy(() => import("./screens/staende/Staende"));
const Vergleich = lazy(() => import("./screens/staende/Vergleich"));
const Modell = lazy(() => import("./screens/modell/Modell"));
const Importe = lazy(() => import("./screens/import/Importe"));
const Bericht = lazy(() => import("./screens/import/Bericht"));
const Bibliothek = lazy(() => import("./screens/bibliothek/Bibliothek"));
const Produkt = lazy(() => import("./screens/bibliothek/Produkt"));
const Profil = lazy(() => import("./screens/Profil"));
const Nutzer = lazy(() => import("./screens/admin/Nutzer"));
const BibliothekPruefen = lazy(() => import("./screens/admin/BibliothekPruefen"));
const Kategorien = lazy(() => import("./screens/admin/Kategorien"));
const Regeln = lazy(() => import("./screens/admin/Regeln"));
const Ernte = lazy(() => import("./screens/admin/Ernte"));
const Maschinen = lazy(() => import("./screens/admin/Maschinen"));

function Wartezeichen() {
  const { t } = useT();
  return (
    <div style={{ height: "60vh", display: "grid", placeItems: "center" }} role="status" aria-label={t.allgemein.laedt}>
      <Spinner size={22} />
    </div>
  );
}

/** GET /me scheiterte ohne 401: Server gerade nicht erreichbar (Deploy, 500, Netz). */
function Stoerung() {
  const { t } = useT();
  const qc = useQueryClient();
  useTitel(t.fehler.stoerungTitel);
  return (
    <div className="blatt blatt-schmal">
      <div className="card" style={{ padding: 28, textAlign: "center" }} role="alert">
        <h1 className="seitentitel">{t.fehler.stoerungTitel}</h1>
        <p className="kt-hilfe" style={{ marginTop: 8 }}>
          {t.fehler.stoerungText}
        </p>
        <div className="reihe" style={{ justifyContent: "center", marginTop: 18 }}>
          <Button type="button" icon="arrow-clockwise" onClick={() => void qc.refetchQueries({ queryKey: schluessel.ich })}>
            {t.fehler.erneut}
          </Button>
        </div>
      </div>
    </div>
  );
}

function NichtGefunden() {
  const { t } = useT();
  const navigate = useNavigate();
  // Ein eigener Fenstertitel: vorher stand im Tab nur „LILASp Katalog" (Prüfung Runde 1).
  useTitel(t.fehler.nichtGefunden);
  return (
    <div className="blatt blatt-schmal">
      <div className="card" style={{ padding: 28, textAlign: "center" }}>
        <h1 className="seitentitel">{t.fehler.nichtGefunden}</h1>
        <div className="reihe" style={{ justifyContent: "center", marginTop: 18 }}>
          <Button type="button" icon="house" onClick={() => navigate("/", { replace: true })}>
            {t.fehler.zurStartseite}
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ Wurzel */

function Wurzel() {
  const status = useAuthStore((s) => s.status);
  const angemeldet = useAuthStore((s) => s.angemeldet);
  const abgemeldet = useAuthStore((s) => s.abgemeldet);
  const gestoert = useAuthStore((s) => s.gestoert);

  // DER EINE Aufruf von GET /me — über dieselbe Abfrage, aus der jeder Bildschirm
  // liest. `aktiv` ist wahr, solange der Status nicht „anonym" ist: beim Start wird
  // gefragt, nach dem Abmelden nicht mehr.
  const { isSuccess, isError, error, refetch } = useIch(status !== "anonym");

  useEffect(() => {
    if (isSuccess) angemeldet();
    // Ein 401 hat den Status über den Riegel in client.ts schon gesetzt. Jeder ANDERE
    // Fehler (Netz, 500, 503 während eines Deploys) ist eine STÖRUNG, keine Abmeldung:
    // Früher hieß er „abgemeldet", die Person stand mit gültigem Keks auf der Anmeldung,
    // und nach dem Deploy fragte niemand mehr nach (Prüfung Runde 2).
    else if (isError) {
      if (istApiFehler(error) && error.status === 401) abgemeldet();
      else gestoert();
    }
  }, [isSuccess, isError, error, angemeldet, abgemeldet, gestoert]);

  // Während der Störung alle 5 s nachfragen: endet der Deploy, geht es von selbst weiter.
  useEffect(() => {
    if (status !== "gestoert") return;
    const uhr = setInterval(() => void refetch(), 5000);
    return () => clearInterval(uhr);
  }, [status, refetch]);

  return (
    <>
      <Outlet />
      {/* GENAU EINMAL in der ganzen Anwendung. */}
      <Toaster />
    </>
  );
}

/* ------------------------------------------------------------- Wächter */

function Geschuetzt() {
  const status = useAuthStore((s) => s.status);
  const ort = useLocation();
  if (status === "unbekannt") return <Wartezeichen />;
  if (status === "gestoert") return <Stoerung />;
  // Nach einem 401 aus irgendeinem Aufruf setzt der Riegel in client.ts den Status auf
  // „anonym", und dieser Zweig führt zur Anmeldung — OHNE Toast. `von` MIT der Suche,
  // damit ein Verweis aus einer Mail nach dem Code derselbe ist.
  if (status !== "angemeldet") return <Navigate to="/anmelden" replace state={{ von: ort.pathname + ort.search }} />;
  return <Outlet />;
}

/** `/admin/*` verlangt zusätzlich die Rolle (§2.3). Kein Toast, keine Erklärung. */
function NurAdmin() {
  const { data: ich } = useIch();
  // `ich` ist einen Wimpernschlag lang undefined, obwohl die Sitzung steht — in dieser
  // Zeit ist die Antwort ein Wartezeichen und keine Umleitung.
  if (!ich) return <Wartezeichen />;
  if (!istAdmin(ich.rolle)) return <Navigate to="/" replace />;
  return <Outlet />;
}

/** Auffangkorb + Nachladeanzeige um einen Bildschirm; `key` setzt beides je Weg zurück. */
function Bildschirm() {
  const ort = useLocation();
  return (
    <ScreenBoundary key={ort.pathname}>
      <Suspense fallback={<Wartezeichen />}>
        <Outlet />
      </Suspense>
    </ScreenBoundary>
  );
}

/* -------------------------------------------------------------- Wegtabelle (§13.5) */

const router = createBrowserRouter([
  {
    element: <Wurzel />,
    children: [
      {
        path: "/anmelden",
        element: (
          <ScreenBoundary>
            <Anmelden />
          </ScreenBoundary>
        ),
      },
      {
        element: <Geschuetzt />,
        children: [
          {
            element: <Shell />,
            children: [
              {
                element: <Bildschirm />,
                children: [
                  { path: "/", element: <Kataloge /> },
                  { path: "/kataloge/neu", element: <NeuerKatalog /> },
                  { path: "/k/:katalogId", element: <Editor /> },
                  { path: "/k/:katalogId/staende", element: <Staende /> },
                  { path: "/k/:katalogId/staende/:standId", element: <Vergleich /> },
                  { path: "/p/:projektId/modell", element: <Modell /> },
                  // Derselbe Bildschirm mit Reiter „Zuordnen" (§13.5) — er liest den
                  // Reiter aus dem Pfad und den Katalog aus `?katalog=<id>`.
                  { path: "/p/:projektId/zuordnen", element: <Modell /> },
                  { path: "/p/:projektId/importe", element: <Importe /> },
                  { path: "/p/:projektId/importe/:importId", element: <Bericht /> },
                  { path: "/bibliothek", element: <Bibliothek /> },
                  { path: "/bibliothek/:produktId", element: <Produkt /> },
                  { path: "/profil", element: <Profil /> },
                  {
                    element: <NurAdmin />,
                    children: [
                      { path: "/admin", element: <Navigate to="/admin/nutzer" replace /> },
                      { path: "/admin/nutzer", element: <Nutzer /> },
                      { path: "/admin/bibliothek", element: <BibliothekPruefen /> },
                      { path: "/admin/kategorien", element: <Kategorien /> },
                      { path: "/admin/regeln", element: <Regeln /> },
                      { path: "/admin/ernte", element: <Ernte /> },
                      { path: "/admin/maschinen", element: <Maschinen /> },
                      // Nur admin (§4.11): weich gelöschte Kataloge sehen und wiederherstellen.
                      { path: "/kataloge/geloescht", element: <Geloescht /> },
                    ],
                  },
                  { path: "*", element: <NichtGefunden /> },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
]);

export function App() {
  return <RouterProvider router={router} />;
}
